import { useQuery } from '@tanstack/react-query';
import { Badge, Button, EmptyState, ErrorState, Field, Input, LoadingState, Status, formatDateTime } from '@lawbee/ui';
import { Activity, Link2, Unlink } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { PlanGate } from '../../components/PlanGate';
import { api, errorMessage } from '../../lib/api';
import { humanize, useApiMutation } from '../../lib/hooks';
import { can, ME_KEY, useMe } from '../../lib/me';
import type { IntegrationsResponse } from '../../lib/types';
import { ReadOnlyNote } from '../app/ops/shared';
import { SettingsCard } from './layout';

const INTEGRATIONS_KEY = ['integrations'] as const;

export function IntegrationsSettingsPage() {
  return (
    <PlanGate feature="integrations">
      <IntegrationsInner />
    </PlanGate>
  );
}

function IntegrationsInner() {
  const me = useMe();
  const canEdit = can(me, 'settings');
  const q = useQuery({ queryKey: INTEGRATIONS_KEY, queryFn: () => api.get<IntegrationsResponse>('/api/integrations') });
  if (q.isLoading) return <LoadingState label="Loading integrations" />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  return (
    <div className="space-y-6">
      {!canEdit ? <ReadOnlyNote /> : null}
      <ClioCard data={q.data} canEdit={canEdit} />
      <EmailFallbackCard address={q.data.emailFallbackAddress} canEdit={canEdit} />
      <EventsCard events={q.data.events} />
    </div>
  );
}

function ClioCard({ data, canEdit }: { data: IntegrationsResponse; canEdit: boolean }) {
  const connect = useApiMutation(() => api.post('/api/integrations/clio/connect'), { success: 'Clio connected', invalidate: [INTEGRATIONS_KEY, ME_KEY] });
  const disconnect = useApiMutation(() => api.post('/api/integrations/clio/disconnect'), { success: 'Clio disconnected', invalidate: [INTEGRATIONS_KEY, ME_KEY] });
  const connected = data.clio.enabled;
  return (
    <SettingsCard
      title={
        <span className="flex flex-wrap items-center gap-2">
          Clio
          {data.clio.mode === 'stub' ? <Badge variant="warning">Stub mode</Badge> : null}
        </span>
      }
      description="Send new matters and client details to Clio so your practice-management system stays the record of truth."
      footer={
        canEdit ? (
          connected ? (
            <Button variant="outline" loading={disconnect.isPending} onClick={() => disconnect.mutate(undefined)}>
              <Unlink /> Disconnect
            </Button>
          ) : (
            <Button loading={connect.isPending} onClick={() => connect.mutate(undefined)}>
              <Link2 /> Connect Clio
            </Button>
          )
        ) : undefined
      }
    >
      <div className="space-y-2 text-sm">
        {connected ? (
          <Status tone="done">
            Connected{' '}
            {data.clio.connectedAt ? (
              <span className="font-normal text-muted-foreground">
                since <span className="figures">{formatDateTime(data.clio.connectedAt)}</span>
              </span>
            ) : null}
          </Status>
        ) : (
          <Status tone="idle" className="text-muted-foreground">
            Not connected
          </Status>
        )}
        {data.clio.mode === 'stub' ? (
          <p className="text-xs text-muted-foreground">No Clio credentials are configured, so syncs are simulated and recorded in the events below.</p>
        ) : null}
      </div>
    </SettingsCard>
  );
}

function EmailFallbackCard({ address, canEdit }: { address: string | null; canEdit: boolean }) {
  const [value, setValue] = useState(address ?? '');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setValue(address ?? ''), [address]);
  const save = useApiMutation((body: { address: string | null }) => api.put('/api/integrations/email-fallback', body), {
    success: 'Email fallback saved',
    invalidate: [INTEGRATIONS_KEY, ME_KEY],
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const v = value.trim();
    if (v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return setError('Enter a valid email address.');
    setError(null);
    save.mutate({ address: v || null }, { onError: (err) => setError(errorMessage(err)) });
  };
  const dirty = value.trim() !== (address ?? '');
  return (
    <form onSubmit={submit} noValidate>
      <SettingsCard
        title="Email fallback"
        description="For any system without a direct integration: LawBee emails a copy of new matters and intakes to this address (for example, your practice-management inbox)."
        footer={
          canEdit ? (
            <Button type="submit" disabled={!dirty} loading={save.isPending}>
              Save
            </Button>
          ) : undefined
        }
      >
        <Field label="Fallback address" htmlFor="int-fallback" error={error} description="Leave empty to turn it off.">
          <Input id="int-fallback" type="email" value={value} disabled={!canEdit} onChange={(e) => setValue(e.target.value)} placeholder="intake@yourpms.example" autoComplete="off" />
        </Field>
      </SettingsCard>
    </form>
  );
}

function EventsCard({ events }: { events: IntegrationsResponse['events'] }) {
  return (
    <SettingsCard title="Recent events" description="The latest 50 integration events.">
      {events.length === 0 ? (
        <EmptyState icon={<Activity />} title="No events yet" className="py-8" />
      ) : (
        <ul className="divide-y divide-border/70">
          {events.map((ev, i) => {
            const provider = typeof ev.provider === 'string' ? ev.provider : '';
            const name = typeof ev.event === 'string' ? ev.event : (ev.type ?? ev.kind ?? 'event');
            const detail = typeof ev.detail === 'string' ? ev.detail : (ev.message ?? '');
            const ok = ev.status === 'ok' || ev.status === 'success';
            return (
              <li key={ev.id ?? i} className="flex flex-col gap-1 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {provider ? `${humanize(provider)}: ` : ''}
                    {humanize(name)}
                  </p>
                  {detail ? <p className="text-[13px] text-muted-foreground">{detail}</p> : null}
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  {ev.status ? (
                    <Status tone={ok ? 'done' : 'risk'} className="text-[12px]">
                      {humanize(ev.status)}
                    </Status>
                  ) : null}
                  {ev.createdAt ? <span className="figures text-xs text-muted-foreground">{formatDateTime(ev.createdAt)}</span> : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </SettingsCard>
  );
}
